/**
 * URL-backed filter/sort/page state for the reporting tables.
 *
 * Modelled directly on `useUrlTimeRange` and `useUrlLocationFilters`. Keeping
 * state in the URL means refresh preserves the view, the view is shareable by
 * pasting the link, and — critically for saved views — applying a view is just
 * "write these params", so views and links are one mechanism rather than two.
 *
 * Three behaviours are inherited from those hooks for concrete reasons:
 *
 *  1. `{ replace: true }` — filter tweaks would otherwise stack a history entry
 *     each, so Back would walk the operator through every keystroke.
 *  2. Defaults are stripped — URLs stay short and self-describing.
 *  3. The page reset is folded into the SAME `setSearchParams` updater as the
 *     filter write. `useUrlTimeRange.ts:82-92` documents why: each updater reads
 *     the LIVE url, not the in-flight one, so a separate `setPage(0)` in the
 *     same tick overwrites the params just written and the filter silently
 *     fails to apply.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  FILTER_PARAM_KEYS,
  parseBookingFilters,
  parseQuoteFilters,
  parseSort,
  serializeFilters,
  serializeSort,
  type BookingFilters,
  type BookingSortField,
  type QuoteFilters,
  type QuoteSortField,
  type TableSort,
} from '@limo/shared'

const DEFAULT_PAGE_SIZE = 20

const BOOKING_SORT_FIELDS: readonly BookingSortField[] = [
  'created_at',
  'pickup_datetime',
  'total_amount',
  'balance_due',
  'customer_name',
  'booking_status',
  'payment_status',
]

const QUOTE_SORT_FIELDS: readonly QuoteSortField[] = [
  'created_at',
  'pickup_datetime',
  'total_amount',
  'expires_at',
  'status',
]

const BOOKING_DEFAULT_SORT: TableSort<BookingSortField> = {
  field: 'created_at',
  direction: 'desc',
}

const QUOTE_DEFAULT_SORT: TableSort<QuoteSortField> = {
  field: 'created_at',
  direction: 'desc',
}

/** Non-filter params this hook owns, cleared alongside filters where relevant. */
const CONTROL_KEYS = ['sort', 'page', 'pageSize'] as const

interface ReportUrlState<F, S extends string> {
  readonly filters: F
  readonly sort: TableSort<S>
  /** 0-indexed, matching `TablePagination`. */
  readonly page: number
  readonly pageSize: number
  readonly setFilters: (next: F) => void
  readonly setSort: (next: TableSort<S>) => void
  readonly setPage: (next: number) => void
  readonly setPageSize: (next: number) => void
  readonly clearFilters: () => void
  /** Apply a saved view: filters + sort together, resetting to page 1. */
  readonly applyView: (filters: F, sort: TableSort<S> | null) => void
}

function parsePositiveInt(raw: string | null, fallback: number): number {
  if (raw === null) return fallback
  const n = Number(raw)
  return Number.isInteger(n) && n >= 0 ? n : fallback
}

/**
 * Shared implementation. `parse` differs per resource so each page only ever
 * sees filters its own table supports.
 */
function useReportUrlState<F extends BookingFilters | QuoteFilters, S extends string>(
  parse: (raw: Record<string, unknown>) => F,
  sortFields: readonly S[],
  defaultSort: TableSort<S>
): ReportUrlState<F, S> {
  const [searchParams, setSearchParams] = useSearchParams()

  // Derived via useMemo keyed on searchParams so the object identity is stable
  // between renders. Rebuilding it every render would produce a new React Query
  // key each time and refetch continuously.
  const filters = useMemo<F>(() => {
    const raw: Record<string, unknown> = {}
    for (const [key, value] of searchParams.entries()) {
      raw[key] = value
    }
    return parse(raw)
  }, [searchParams, parse])

  const sort = useMemo<TableSort<S>>(
    () => parseSort(searchParams.get('sort'), sortFields, defaultSort),
    [searchParams, sortFields, defaultSort]
  )

  const page = useMemo(() => parsePositiveInt(searchParams.get('page'), 0), [searchParams])
  const pageSize = useMemo(
    () => parsePositiveInt(searchParams.get('pageSize'), DEFAULT_PAGE_SIZE),
    [searchParams]
  )

  /**
   * Write filters and reset paging in ONE updater.
   *
   * Splitting these into two setSearchParams calls is the race described in the
   * file header — the second call reads the pre-update URL and drops the
   * filters written by the first.
   */
  const setFilters = useCallback(
    (next: F) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          for (const key of FILTER_PARAM_KEYS) params.delete(key)
          for (const [key, value] of Object.entries(serializeFilters(next))) {
            params.set(key, value)
          }
          // A new filter set invalidates the current offset ("page 40 of 1").
          params.delete('page')
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setSort = useCallback(
    (next: TableSort<S>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          params.set('sort', serializeSort(next))
          params.delete('page')
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setPage = useCallback(
    (next: number) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          if (next <= 0) params.delete('page')
          else params.set('page', String(next))
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setPageSize = useCallback(
    (next: number) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          if (next === DEFAULT_PAGE_SIZE) params.delete('pageSize')
          else params.set('pageSize', String(next))
          params.delete('page')
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        for (const key of FILTER_PARAM_KEYS) params.delete(key)
        params.delete('page')
        return params
      },
      { replace: true }
    )
  }, [setSearchParams])

  const applyView = useCallback(
    (nextFilters: F, nextSort: TableSort<S> | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          for (const key of [...FILTER_PARAM_KEYS, ...CONTROL_KEYS]) params.delete(key)
          for (const [key, value] of Object.entries(serializeFilters(nextFilters))) {
            params.set(key, value)
          }
          if (nextSort) params.set('sort', serializeSort(nextSort))
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  return {
    filters,
    sort,
    page,
    pageSize,
    setFilters,
    setSort,
    setPage,
    setPageSize,
    clearFilters,
    applyView,
  }
}

export function useUrlBookingFilters(): ReportUrlState<BookingFilters, BookingSortField> {
  return useReportUrlState(parseBookingFilters, BOOKING_SORT_FIELDS, BOOKING_DEFAULT_SORT)
}

export function useUrlQuoteFilters(): ReportUrlState<QuoteFilters, QuoteSortField> {
  return useReportUrlState(parseQuoteFilters, QUOTE_SORT_FIELDS, QUOTE_DEFAULT_SORT)
}

export { DEFAULT_PAGE_SIZE }
